import { useModal, useToastr } from '@farmshare/ui-components';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Add as AddIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';

import {
  EnumJobTagSpecies,
  useJobTagCreateOneMutation,
  useJobTagRemoveByIdMutation,
  useJobTagUpdateByIdMutation,
} from '../../lib/graphql';

import { DEFAULT_JOB_TAG_ICON, type JobTagIconKey } from './icons';
import { userFacingError } from 'lib/user-facing-error';
import { IconSwatchPicker } from './icon-swatch-picker';
import { JobTagSettingsRow } from './job-tag-settings-row';
import { SpeciesSelect } from './species-select';
import { type JobTagRow, useJobTags } from './use-job-tags';

/**
 * The unique `{vendor, name}` index rejects a duplicate with a raw
 * `E11000 duplicate key…` string. That's an implementation detail, not
 * something to put in front of a processor.
 * @param err The thrown Apollo/GraphQL error.
 * @returns A message safe to show in a toast.
 */
function errorMessage(err: unknown): string {
  const message = (err as Error)?.message ?? '';
  return /duplicate key|E11000/i.test(message)
    ? 'You already have a tag with that name.'
    : userFacingError(err, 'Your tags haven’t changed. Try again.');
}

/**
 * Settings panel for the vendor's job tags. Tags created here become the
 * options in the job-page and booking pickers, the icons on calendar events
 * and job rows, and the entries in the calendar's tag filter.
 */
export function JobTagSettings() {
  const { push } = useToastr();
  const { ask } = useModal();
  const { vendorId, tags, loading, refetchQueries } = useJobTags();

  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState<JobTagIconKey>(DEFAULT_JOB_TAG_ICON);
  const [newSpecies, setNewSpecies] = useState<EnumJobTagSpecies[]>([]);

  const [createTag, createOp] = useJobTagCreateOneMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [updateTag] = useJobTagUpdateByIdMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [removeTag] = useJobTagRemoveByIdMutation({
    refetchQueries,
    awaitRefetchQueries: true,
    // Refetching the tag LIST fixes this panel and the pickers, but every job
    // already in the cache — the calendar, the jobs table, an open job page —
    // still points at the deleted tag and would keep drawing it until each of
    // those queries happened to refetch. Evicting the entity removes it from
    // all of them at once: Apollo drops a dangling reference out of a list
    // when it reads it.
    update: (cache, { data }) => {
      const recordId = data?.jobTagRemoveById?.recordId;
      if (!recordId) return;
      cache.evict({
        id: cache.identify({ __typename: 'JobTag', _id: recordId }),
      });
      cache.gc();
    },
  });

  const pushError = useCallback(
    (err: unknown) =>
      push({
        title: "Couldn't update your tags",
        body: errorMessage(err),
        bg: 'danger',
        delay: 4000,
      }),
    [push],
  );

  const handleSave = useCallback(
    async (id: string, patch: Partial<JobTagRow>) => {
      try {
        await updateTag({ variables: { id, record: patch } });
        return true;
      } catch (err) {
        pushError(err);
        return false;
      }
    },
    [updateTag, pushError],
  );

  const handleCreate = useCallback(async () => {
    const name = newName.trim();
    if (!name || !vendorId) return;
    try {
      await createTag({
        variables: {
          record: {
            vendor: vendorId,
            name,
            icon: newIcon,
            species: newSpecies,
            isEnabled: true,
            sortOrder: tags.length,
          },
        },
      });
      setNewName('');
      setNewSpecies([]);
      push({
        title: `Tag "${name}" added`,
        body: 'You can put it on jobs now.',
        bg: 'primary',
        delay: 3000,
      });
    } catch (err) {
      pushError(err);
    }
  }, [
    newName,
    newIcon,
    newSpecies,
    vendorId,
    tags.length,
    createTag,
    push,
    pushError,
  ]);

  const handleDelete = useCallback(
    (tag: JobTagRow) => {
      ask({
        type: 'ask',
        title: 'Delete tag?',
        body: `"${tag.name}" will be permanently deleted and taken off every job carrying it. Disable it instead if you only want to stop putting it on new jobs.`,
        yesText: 'Delete',
        yesIcon: faTrash,
        onConfirm: async () => {
          try {
            await removeTag({ variables: { id: tag._id } });
            push({
              title: `Tag "${tag.name}" deleted`,
              body: 'You can’t add it to jobs anymore.',
              bg: 'primary',
              delay: 3000,
            });
          } catch (err) {
            pushError(err);
          }
        },
      });
    },
    [ask, removeTag, push, pushError],
  );

  if (loading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Typography variant="body2" color="text.secondary">
        Tags mark jobs that need handling differently — a farm kill, a rush
        order, a religious slaughter. Each tag carries an icon that shows on the
        calendar and in your job list, and can be limited to the species it
        applies to. Disabling a tag retires it — it drops out of the pickers,
        but jobs already carrying it keep showing it.
      </Typography>

      {tags.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No tags yet. Add your first one below.
        </Typography>
      ) : (
        tags.map((tag) => (
          <JobTagSettingsRow
            key={tag._id}
            tag={tag}
            onSave={handleSave}
            onDelete={handleDelete}
          />
        ))
      )}

      <Paper variant="outlined" sx={{ p: 1.5, borderStyle: 'dashed' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <IconSwatchPicker
            value={newIcon}
            label="Icon for the new tag"
            onChange={setNewIcon}
          />
          <TextField
            size="small"
            label="New tag"
            placeholder="e.g. Farm Kill"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreate();
              }
            }}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <SpeciesSelect value={newSpecies} onChange={setNewSpecies} />
          <Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              disabled={!newName.trim() || createOp.loading}
              onClick={() => handleCreate()}
            >
              Add
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
}
